import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, Save, Plus, Trash2, Image as ImageIcon, Briefcase, AlertCircle, FileText, ShieldAlert, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { getActiveStudioId, isSuperAdmin } from "@/lib/api";
import BottomNav, { Sidebar } from "@/components/Navigation";
import { useBrand } from "@/lib/brand";
import { TshirtSizesManager } from "@/components/settings/TshirtSizesManager";
import { GoogleSheetsSettings } from "@/components/settings/GoogleSheetsSettings";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Shallow-compare helper for nested objects/arrays — sufficient for our form shape.
const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const StudioSettings = ({ user, onLogout }) => {
  const { organization_name, display_name, refresh: refreshBrand, setPrimaryColor } = useBrand();
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState(getActiveStudioId() || "");
  const [studio, setStudio] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orgForm, setOrgForm] = useState({ organization_name: "", display_name: "", terms_classes: "", terms_rentals: "", default_country_code: "" });
  const canEditOrg = isSuperAdmin(user);

  // Pristine snapshots for dirty detection
  const [initialStudio, setInitialStudio] = useState(null);
  const [initialOrg, setInitialOrg] = useState({ organization_name: "", display_name: "", terms_classes: "", terms_rentals: "", default_country_code: "" });

  // Pending navigation request (in-app)
  const [pendingNav, setPendingNav] = useState(null);
  // Refs for accessing latest dirty state inside event handlers
  const isDirtyRef = useRef(false);
  const isSavingRef = useRef(false);
  const studioIdSwitchPending = useRef(null);

  useEffect(() => {
    setOrgForm((p) => ({ ...p, organization_name, display_name }));
    setInitialOrg((p) => ({ ...p, organization_name, display_name }));
  }, [organization_name, display_name]);

  // Fetch terms (Classes + Rentals) once on mount
  useEffect(() => {
    api.get("/organization").then((r) => {
      const o = r.data?.organization || {};
      const tc = o.terms_classes || "";
      const tr = o.terms_rentals || "";
      const cc = o.default_country_code || "";
      setOrgForm((p) => ({ ...p, terms_classes: tc, terms_rentals: tr, default_country_code: cc }));
      setInitialOrg((p) => ({ ...p, terms_classes: tc, terms_rentals: tr, default_country_code: cc }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/studios").then((r) => {
      const list = r.data.studios || [];
      setStudios(list);
      if (!studioId && list.length > 0) setStudioId(list[0].studio_id);
    });
  }, []);

  useEffect(() => {
    if (!studioId) return;
    api.get(`/studios/${studioId}`).then((r) => {
      setStudio(r.data.studio);
      setInitialStudio(r.data.studio);
    });
  }, [studioId]);

  // === Dirty detection ===
  const studioFieldsToWatch = ["name","location","address","contact_number","email","rooms","working_hours","branding","allowed_payment_modes","default_charge_mode","currency"];
  const isStudioDirty = useMemo(() => {
    if (!studio || !initialStudio) return false;
    return studioFieldsToWatch.some((k) => !sameValue(studio[k], initialStudio[k]));
  }, [studio, initialStudio]);

  const isOrgDirty = useMemo(() => (
    canEditOrg && (
      (orgForm.organization_name || "") !== (initialOrg.organization_name || "") ||
      (orgForm.display_name || "") !== (initialOrg.display_name || "") ||
      (orgForm.terms_classes || "") !== (initialOrg.terms_classes || "") ||
      (orgForm.terms_rentals || "") !== (initialOrg.terms_rentals || "") ||
      (orgForm.default_country_code || "") !== (initialOrg.default_country_code || "")
    )
  ), [orgForm, initialOrg, canEditOrg]);

  const isDirty = isStudioDirty || isOrgDirty;
  isDirtyRef.current = isDirty;

  // === Browser navigation guard (close tab / refresh / back) ===
  useEffect(() => {
    const handler = (e) => {
      if (isDirtyRef.current && !isSavingRef.current) {
        e.preventDefault();
        e.returnValue = "";  // legacy Chromium / Firefox
        return "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // === In-app navigation guard: intercept clicks on internal links/buttons ===
  useEffect(() => {
    const handleClick = (e) => {
      if (!isDirtyRef.current || isSavingRef.current) return;
      // Find nearest <a> or element with data-nav-href
      const link = e.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href");
      // Only block in-app routes — let external links / hash links through
      if (!href || href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("#") || href === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNav(href);
    };
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  if (!studio) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Loading studio settings...</p>
      </div>
    );
  }

  const updateField = (key, value) => setStudio({ ...studio, [key]: value });
  const updateBranding = (key, value) => setStudio({ ...studio, branding: { ...(studio.branding || {}), [key]: value } });
  const updateWorkingHours = (day, key, value) => {
    const wh = { ...(studio.working_hours || {}) };
    wh[day] = { ...(wh[day] || {}), [key]: value };
    setStudio({ ...studio, working_hours: wh });
  };
  const toggleDayClosed = (day) => {
    const wh = { ...(studio.working_hours || {}) };
    if (wh[day] && wh[day].closed) {
      delete wh[day].closed;
    } else {
      wh[day] = { ...(wh[day] || {}), closed: true };
    }
    setStudio({ ...studio, working_hours: wh });
  };

  const addRoom = () => {
    const rooms = [...(studio.rooms || [])];
    rooms.push({
      room_id: crypto.randomUUID(),
      name: `Studio ${rooms.length + 1}`,
      capacity: 20,
      display_order: rooms.length,
    });
    setStudio({ ...studio, rooms });
  };
  const updateRoom = (idx, key, value) => {
    const rooms = [...studio.rooms];
    rooms[idx] = { ...rooms[idx], [key]: value };
    setStudio({ ...studio, rooms });
  };
  const removeRoom = (idx) => {
    const rooms = studio.rooms.filter((_, i) => i !== idx);
    // Re-stamp display_order so the saved doc stays clean after a removal.
    rooms.forEach((r, i) => { r.display_order = i; });
    setStudio({ ...studio, rooms });
  };
  // Feature 1 — Move Up / Move Down ordering (Q1=b). Pure swap, no ID mutation.
  const moveRoom = (idx, direction) => {
    const rooms = [...(studio.rooms || [])];
    const target = idx + (direction === "up" ? -1 : 1);
    if (target < 0 || target >= rooms.length) return;
    [rooms[idx], rooms[target]] = [rooms[target], rooms[idx]];
    // Re-stamp display_order from the new array order — this is what gets
    // persisted and what the calendar / pickers will sort by next.
    rooms.forEach((r, i) => { r.display_order = i; });
    setStudio({ ...studio, rooms });
  };

  const togglePaymentMode = (mode) => {
    const modes = studio.allowed_payment_modes || [];
    const next = modes.includes(mode) ? modes.filter((m) => m !== mode) : [...modes, mode];
    setStudio({ ...studio, allowed_payment_modes: next });
  };

  const handleSaveOrg = async () => {
    if (!canEditOrg) return; // skip silently for non-super-admins
    if (!orgForm.organization_name?.trim() || !orgForm.display_name?.trim()) {
      throw new Error("Organization Name and Display Name are required");
    }
    await api.patch("/organization", {
      organization_name: orgForm.organization_name.trim(),
      display_name: orgForm.display_name.trim(),
      terms_classes: orgForm.terms_classes ?? "",
      terms_rentals: orgForm.terms_rentals ?? "",
      default_country_code: (orgForm.default_country_code || "").trim(),
    });
    await refreshBrand();
  };

  const handleSave = async () => {
    isSavingRef.current = true;
    setSaving(true);
    try {
      // Save both organization (if super admin and changed) and studio settings together
      if (canEditOrg && isOrgDirty) {
        await handleSaveOrg();
        setInitialOrg({ ...orgForm });
      }
      if (isStudioDirty) {
        await api.patch(`/studios/${studio.studio_id}/settings`, {
          name: studio.name,
          location: studio.location,
          address: studio.address,
          contact_number: studio.contact_number,
          email: studio.email,
          rooms: studio.rooms,
          working_hours: studio.working_hours,
          branding: studio.branding,
          allowed_payment_modes: studio.allowed_payment_modes,
          default_charge_mode: studio.default_charge_mode,
          currency: studio.currency,
        });
        // If the user is editing the currently-active studio, refresh the brand color immediately
        const activeId = localStorage.getItem("active_studio_id");
        if (activeId === studio.studio_id && studio.branding?.primary_color) {
          setPrimaryColor(studio.branding.primary_color);
          const cached = JSON.parse(localStorage.getItem("active_studio") || "{}");
          cached.branding = { ...(cached.branding || {}), primary_color: studio.branding.primary_color };
          localStorage.setItem("active_studio", JSON.stringify(cached));
        }
        // Mark new pristine baseline
        setInitialStudio(studio);
      }
      toast.success("All settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to save");
    } finally {
      setSaving(false);
      isSavingRef.current = false;
    }
  };

  const tryChangeStudio = (newId) => {
    if (newId === studioId) return;
    if (isDirty) {
      studioIdSwitchPending.current = newId;
      setPendingNav("__switch_studio__");
      return;
    }
    setStudioId(newId);
  };

  const discardAndProceed = () => {
    // Restore pristine values
    if (initialStudio) setStudio(initialStudio);
    setOrgForm({ ...initialOrg });
    isDirtyRef.current = false;
    const target = pendingNav;
    setPendingNav(null);
    if (target === "__switch_studio__" && studioIdSwitchPending.current) {
      const next = studioIdSwitchPending.current;
      studioIdSwitchPending.current = null;
      setStudioId(next);
    } else if (target) {
      // Use a tiny timeout so React clears the dialog before we navigate
      setTimeout(() => { window.location.href = target; }, 0);
    }
  };

  const cancelNav = () => {
    setPendingNav(null);
    studioIdSwitchPending.current = null;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pb-24 md:pb-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Studio Settings</h1>
              <p className="text-sm text-slate-500 mt-1">Manage studio profile, spaces, working hours and finance defaults.</p>
            </div>
            <div className="flex items-center gap-3">
              {studios.length > 1 && (
                <select
                  data-testid="studio-settings-selector"
                  value={studioId}
                  onChange={(e) => tryChangeStudio(e.target.value)}
                  className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm"
                >
                  {studios.map((s) => (
                    <option key={s.studio_id} value={s.studio_id}>{s.name}</option>
                  ))}
                </select>
              )}
              <Button
                data-testid="studio-settings-save-button"
                onClick={handleSave}
                disabled={saving || !isDirty}
                className="rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: !isDirty ? "#cbd5e1" : "var(--brand-primary)", color: "white" }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : isDirty ? "Save Changes" : "Saved"}
              </Button>
            </div>
          </div>

          {/* Sticky "Unsaved changes" banner */}
          <AnimatePresence>
            {isDirty && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                data-testid="unsaved-banner"
                className="sticky top-2 z-10 flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50/95 backdrop-blur px-4 py-2.5 text-sm shadow-sm"
              >
                <div className="flex items-center gap-2 text-amber-900">
                  <AlertCircle className="w-4 h-4" />
                  <span>You have unsaved changes.</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="discard-changes-button"
                    variant="outline"
                    size="sm"
                    onClick={() => { if (initialStudio) setStudio(initialStudio); setOrgForm({ ...initialOrg }); }}
                    className="h-8"
                  >
                    Discard
                  </Button>
                  <Button
                    data-testid="banner-save-button"
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    className="h-8 rounded-full text-white"
                    style={{ backgroundColor: "var(--brand-primary)" }}
                  >
                    {saving ? "Saving..." : "Save Now"}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Organization (top-level) */}
          <Section
            title="Organization"
            icon={<Briefcase className="w-5 h-5 text-violet-600" />}
          >
            {!canEditOrg && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
                Only Super Admin can change organization details.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Organization Name">
                <Input
                  data-testid="org-name-input"
                  placeholder="e.g., Twins Dance Art Studios"
                  value={orgForm.organization_name}
                  onChange={(e) => setOrgForm({ ...orgForm, organization_name: e.target.value })}
                  disabled={!canEditOrg}
                />
              </Field>
              <Field label="Display Name (replaces app branding)">
                <Input
                  data-testid="org-display-name-input"
                  placeholder="e.g., TDAS"
                  value={orgForm.display_name}
                  onChange={(e) => setOrgForm({ ...orgForm, display_name: e.target.value })}
                  disabled={!canEditOrg}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Shown in sidebar, login page, and app titles.
                </p>
              </Field>
              <Field label="Default Country Code">
                <Input
                  data-testid="org-country-code-input"
                  placeholder="+91"
                  value={orgForm.default_country_code}
                  onChange={(e) => setOrgForm({ ...orgForm, default_country_code: e.target.value })}
                  disabled={!canEditOrg}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Prepended to 10-digit local mobile numbers when sending WhatsApp / SMS messages.
                </p>
              </Field>
            </div>

            <div className="mt-6 pt-5 border-t border-slate-100">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">T-Shirt Sizes</h4>
              <TshirtSizesManager canEdit={canEditOrg} />
            </div>

            <div className="mt-6 pt-5 border-t border-slate-100 space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-violet-600" />
                <h4 className="text-sm font-semibold text-slate-700">Receipt Terms & Conditions</h4>
              </div>
              <p className="text-xs text-slate-500 -mt-2">
                Printed on the bottom of every payment receipt PDF. One bullet per line.
              </p>

              <Field label="For Class Receipts (Enrollment & Renewal)">
                <Textarea
                  data-testid="terms-classes-input"
                  rows={5}
                  value={orgForm.terms_classes}
                  onChange={(e) => setOrgForm({ ...orgForm, terms_classes: e.target.value })}
                  placeholder={"e.g.\nFees once paid are non-refundable.\nMissed classes can be made up within 30 days."}
                  disabled={!canEditOrg}
                  className="rounded-xl text-sm font-mono"
                />
              </Field>

              <Field label="For Rental Receipts">
                <Textarea
                  data-testid="terms-rentals-input"
                  rows={5}
                  value={orgForm.terms_rentals}
                  onChange={(e) => setOrgForm({ ...orgForm, terms_rentals: e.target.value })}
                  placeholder={"e.g.\nRescheduling allowed up to 24 hours prior to slot.\nCustomer is liable for damage to studio equipment."}
                  disabled={!canEditOrg}
                  className="rounded-xl text-sm font-mono"
                />
              </Field>
            </div>
          </Section>

          {/* Google Sheets reporting (Super Admin only) */}
          {canEditOrg && (
            <Section title="Google Sheets Reporting" icon={<FileText className="w-5 h-5 text-violet-600" />}>
              <GoogleSheetsSettings canEdit={canEditOrg} />
            </Section>
          )}

          {/* Danger Zone — Factory Reset (Super Admin only) */}
          {canEditOrg && (
            <FactoryResetSection />
          )}

          {/* Profile */}
          <Section title="Studio Profile" icon={<Building2 className="w-5 h-5 text-violet-600" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Studio Name">
                <Input data-testid="studio-name-input" value={studio.name || ""} onChange={(e) => updateField("name", e.target.value)} />
              </Field>
              <Field label="Location">
                <Input data-testid="studio-location-input" value={studio.location || ""} onChange={(e) => updateField("location", e.target.value)} />
              </Field>
              <Field label="Address">
                <Input data-testid="studio-address-input" value={studio.address || ""} onChange={(e) => updateField("address", e.target.value)} />
              </Field>
              <Field label="Contact Number">
                <Input data-testid="studio-contact-input" value={studio.contact_number || ""} onChange={(e) => updateField("contact_number", e.target.value)} />
              </Field>
              <Field label="Email">
                <Input data-testid="studio-email-input" type="email" value={studio.email || ""} onChange={(e) => updateField("email", e.target.value)} />
              </Field>
              <Field label="Currency">
                <Input data-testid="studio-currency-input" value={studio.currency || "INR"} onChange={(e) => updateField("currency", e.target.value)} />
              </Field>
            </div>
          </Section>

          {/* Branding */}
          <Section title="Branding" icon={<ImageIcon className="w-5 h-5 text-violet-600" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Logo URL">
                <Input
                  data-testid="branding-logo-input"
                  placeholder="https://..."
                  value={studio.branding?.logo_url || ""}
                  onChange={(e) => updateBranding("logo_url", e.target.value)}
                />
              </Field>
              <Field label="Primary Color">
                <div className="flex items-center gap-2">
                  <input
                    data-testid="branding-color-picker"
                    type="color"
                    value={studio.branding?.primary_color || "#7C3AED"}
                    onChange={(e) => updateBranding("primary_color", e.target.value)}
                    className="h-10 w-14 rounded cursor-pointer border border-slate-200"
                  />
                  <Input
                    data-testid="branding-color-input"
                    value={studio.branding?.primary_color || "#7C3AED"}
                    onChange={(e) => updateBranding("primary_color", e.target.value)}
                  />
                </div>
              </Field>
            </div>
            {studio.branding?.logo_url && (
              <div className="mt-4 p-3 bg-slate-50 rounded-xl flex items-center gap-3">
                <img src={studio.branding.logo_url} alt="Logo preview" className="w-12 h-12 object-contain rounded" onError={(e) => (e.target.style.display = "none")} />
                <span className="text-xs text-slate-500">Logo preview</span>
              </div>
            )}
          </Section>

          {/* Spaces */}
          <Section
            title="Spaces (Rooms)"
            icon={<Building2 className="w-5 h-5 text-violet-600" />}
            action={
              <Button data-testid="add-room-button" size="sm" variant="outline" onClick={addRoom}>
                <Plus className="w-4 h-4 mr-1" /> Add Room
              </Button>
            }
          >
            {(!studio.rooms || studio.rooms.length === 0) && (
              <p className="text-sm text-slate-500">No rooms added yet.</p>
            )}
            <p className="text-xs text-slate-500 mb-3">
              Order here is how rooms appear in Calendar columns. Use the arrows on each row to reorder.
            </p>
            <div className="space-y-3">
              {(studio.rooms || []).map((room, idx) => (
                <div key={room.room_id || idx} className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-12 md:col-span-6">
                    <Label className="text-xs">Room Name</Label>
                    <Input data-testid={`room-name-${idx}`} value={room.name || ""} onChange={(e) => updateRoom(idx, "name", e.target.value)} />
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Label className="text-xs">Capacity</Label>
                    <Input data-testid={`room-capacity-${idx}`} type="number" min={1} value={room.capacity || 0} onChange={(e) => updateRoom(idx, "capacity", parseInt(e.target.value) || 0)} />
                  </div>
                  <div className="col-span-6 md:col-span-2 flex gap-1">
                    <Button
                      data-testid={`room-move-up-${idx}`}
                      variant="outline"
                      size="sm"
                      onClick={() => moveRoom(idx, "up")}
                      disabled={idx === 0}
                      className="flex-1 disabled:opacity-30"
                      title="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`room-move-down-${idx}`}
                      variant="outline"
                      size="sm"
                      onClick={() => moveRoom(idx, "down")}
                      disabled={idx === (studio.rooms || []).length - 1}
                      className="flex-1 disabled:opacity-30"
                      title="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Button data-testid={`remove-room-${idx}`} variant="outline" size="sm" onClick={() => removeRoom(idx)} className="w-full text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Working Hours */}
          <Section title="Working Hours" icon={<Building2 className="w-5 h-5 text-violet-600" />}>
            <div className="space-y-2">
              {DAYS.map((day) => {
                const wh = studio.working_hours?.[day] || {};
                return (
                  <div key={day} className="grid grid-cols-12 gap-3 items-center py-2 border-b border-slate-100 last:border-0">
                    <div className="col-span-3 md:col-span-2 font-medium text-slate-700">{day}</div>
                    <div className="col-span-3">
                      <Input
                        data-testid={`wh-start-${day}`}
                        type="time"
                        disabled={wh.closed}
                        value={wh.start || ""}
                        onChange={(e) => updateWorkingHours(day, "start", e.target.value)}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        data-testid={`wh-end-${day}`}
                        type="time"
                        disabled={wh.closed}
                        value={wh.end || ""}
                        onChange={(e) => updateWorkingHours(day, "end", e.target.value)}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-4 flex items-center gap-2 text-xs">
                      <input data-testid={`wh-closed-${day}`} type="checkbox" checked={!!wh.closed} onChange={() => toggleDayClosed(day)} />
                      <span className="text-slate-600">Closed</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Financial Defaults */}
          <Section title="Financial Defaults" icon={<Building2 className="w-5 h-5 text-violet-600" />}>
            <Field label="Default Charge Mode">
              <select
                data-testid="charge-mode-select"
                value={studio.default_charge_mode || "full"}
                onChange={(e) => updateField("default_charge_mode", e.target.value)}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="full">Full Fee</option>
                <option value="prorated">Prorated</option>
              </select>
            </Field>
            <div className="mt-4">
              <Label className="text-xs">Allowed Payment Modes</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {["upi", "cash", "bank_transfer", "card", "cheque"].map((mode) => {
                  const active = (studio.allowed_payment_modes || []).includes(mode);
                  return (
                    <button
                      key={mode}
                      data-testid={`pm-toggle-${mode}`}
                      onClick={() => togglePaymentMode(mode)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${active ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-violet-300"}`}
                    >
                      {mode.replace("_", " ")}
                    </button>
                  );
                })}
              </div>
            </div>
          </Section>
        </motion.div>
      </main>
      <BottomNav user={user} onLogout={onLogout} />

      {/* Leave page confirmation */}
      <Dialog open={!!pendingNav} onOpenChange={(o) => { if (!o) cancelNav(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Unsaved changes
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            You have unsaved changes. If you leave now, those changes will be lost.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelNav} data-testid="leave-cancel">
              Stay on Page
            </Button>
            <Button
              variant="outline"
              data-testid="leave-discard"
              onClick={discardAndProceed}
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              Discard & Leave
            </Button>
            <Button
              data-testid="leave-save"
              onClick={async () => { await handleSave(); const t = pendingNav; cancelNav(); if (t === "__switch_studio__" && studioIdSwitchPending.current) { setStudioId(studioIdSwitchPending.current); studioIdSwitchPending.current = null; } else if (t) { window.location.href = t; } }}
              className="text-white"
              style={{ backgroundColor: "var(--brand-primary)" }}
            >
              Save & Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Section = ({ title, icon, action, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

const FactoryResetSection = () => {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [running, setRunning] = useState(false);
  const [orphanInfo, setOrphanInfo] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanDeleting, setOrphanDeleting] = useState(false);
  const REQUIRED = "WIPE_EVERYTHING_AND_RESEED";

  const handleOrphanScan = async () => {
    setOrphanLoading(true);
    try {
      const res = await api.post("/admin/cleanup-orphan-sessions?dry_run=true");
      setOrphanInfo(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setOrphanLoading(false);
    }
  };

  const handleOrphanDelete = async () => {
    if (!orphanInfo || orphanInfo.found === 0) return;
    if (!window.confirm(`Delete ${orphanInfo.found} orphan session(s)? They will not appear on the calendar after this.`)) return;
    setOrphanDeleting(true);
    try {
      const res = await api.post("/admin/cleanup-orphan-sessions?dry_run=false");
      toast.success(`Deleted ${res.data?.deleted || 0} orphan session(s).`);
      setOrphanInfo(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setOrphanDeleting(false);
    }
  };

  const handleReset = async () => {
    if (phrase !== REQUIRED) return;
    setRunning(true);
    try {
      const res = await api.post("/admin/factory-reset", { confirmation: REQUIRED });
      const dropped = res.data?.dropped || {};
      const totalDocs = Object.values(dropped).reduce((s, v) => s + (typeof v === "number" ? v : 0), 0);
      toast.success(`Factory reset complete — wiped ${totalDocs.toLocaleString()} documents. Logging out…`);
      // Force fresh login to pick up reseeded state
      setTimeout(() => {
        localStorage.clear();
        window.location.href = "/";
      }, 1800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Factory reset failed");
      setRunning(false);
    }
  };

  return (
    <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5" data-testid="danger-zone-section">
      <div className="flex items-center gap-2 mb-2">
        <ShieldAlert className="w-5 h-5 text-red-600" />
        <h2 className="text-base font-semibold text-red-900">Danger Zone — Factory Reset</h2>
      </div>
      <p className="text-sm text-red-800 mb-4">
        Permanently delete <strong>every operational record</strong> — students, batches, payments, attendance,
        bookings, audit logs, etc. Only the Google Sheets workbook ID will be preserved. The Super Admin
        account, password, and a Default Studio will be recreated. <strong>This cannot be undone.</strong>
      </p>

      {/* Orphan session cleanup — non-destructive utility */}
      <div className="bg-white rounded-xl border border-red-100 p-4 mb-4">
        <p className="text-sm font-semibold text-slate-900 mb-1">Clean up orphan sessions</p>
        <p className="text-xs text-slate-600 mb-3">
          Removes leftover class sessions whose date is before their batch's start_date.
          Safe to run anytime. Dry-run first.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            data-testid="orphan-scan-btn"
            variant="outline"
            onClick={handleOrphanScan}
            disabled={orphanLoading}
            className="rounded-lg text-sm"
          >
            {orphanLoading ? "Scanning…" : "Scan for orphans"}
          </Button>
          {orphanInfo && (
            <>
              <span className="text-sm text-slate-700">
                Found <strong>{orphanInfo.found}</strong> orphan session{orphanInfo.found === 1 ? "" : "s"}.
              </span>
              {orphanInfo.found > 0 && (
                <Button
                  data-testid="orphan-delete-btn"
                  variant="outline"
                  onClick={handleOrphanDelete}
                  disabled={orphanDeleting}
                  className="rounded-lg text-sm text-red-700 border-red-300 hover:bg-red-50"
                >
                  {orphanDeleting ? "Deleting…" : `Delete ${orphanInfo.found}`}
                </Button>
              )}
            </>
          )}
        </div>
        {orphanInfo?.sample?.length > 0 && (
          <ul className="mt-3 text-[11px] text-slate-500 space-y-0.5 max-h-32 overflow-y-auto">
            {orphanInfo.sample.slice(0, 10).map((s) => (
              <li key={s.session_id}>
                {s.batch_name} — session {s.session_date?.split("T")[0]} (batch starts {s.batch_start_date?.split?.("T")[0]})
              </li>
            ))}
            {orphanInfo.found > 10 && <li>…and {orphanInfo.found - 10} more</li>}
          </ul>
        )}
      </div>

      <Button
        data-testid="factory-reset-open-btn"
        variant="outline"
        className="border-red-400 text-red-700 hover:bg-red-100"
        onClick={() => { setPhrase(""); setOpen(true); }}
      >
        <Trash2 className="w-4 h-4 mr-2" /> Wipe All Data & Reseed
      </Button>

      <Dialog open={open} onOpenChange={(v) => !running && setOpen(v)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert className="w-5 h-5" /> Confirm Factory Reset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              You are about to wipe every record in this database. After this, the app will return to a
              brand-new state with one Super Admin (<code>9999999999</code> / <code>Admin@123</code>)
              and a Default Studio.
            </p>
            <p className="text-red-700 font-medium">
              Your Google Sheets workbook ID will be preserved. Everything else is gone.
            </p>
            <Field label={`Type "${REQUIRED}" to confirm`}>
              <Input
                data-testid="factory-reset-confirm-input"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={REQUIRED}
                disabled={running}
              />
            </Field>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={running} data-testid="factory-reset-cancel-btn">
              Cancel
            </Button>
            <Button
              data-testid="factory-reset-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={phrase !== REQUIRED || running}
              onClick={handleReset}
            >
              {running ? "Wiping…" : "Yes, wipe everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudioSettings;
